import { AuthorsList } from '@/components/AuthorsList';
import { Layout } from '@/components/Layout';
import { SchemaMarkup } from '@/components/SchemaMarkup';
import { getCurrentBrand } from '@/utils/getCurrentBrand';
import { getUrl } from '@/utils/getUrl';
import { CLOUDFLARE_TTL } from '@/utils/ttl';
import { withCacheControlHeaders } from '@/utils/withCacheControlHeaders';
import { withSerializationGuard } from '@/utils/withSerializationGuard';
import { AuthorsListAuthor } from 'base/components/AuthorsList';
import { getLabradorConfig } from 'lib/configs/labrador';
import { getOcelotConfig } from 'lib/configs/ocelot';
import { ConsolidatedAuthor, getEligibleToShowAuthors } from 'lib/labrador/author-page';
import { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';

interface PageAuthorsProps {
  authors: ConsolidatedAuthor[];
  headline?: string;
  description?: string;
  titleSuffix?: string;
}

const PageAuthors: NextPage<PageAuthorsProps> = ({ authors, headline, description, titleSuffix }) => {
  const brandName = getCurrentBrand()?.name ?? '';
  const publishedUrl = `/vara-skribenter`;
  const canonical = getUrl(publishedUrl)?.href;

  const authorsList = authors.map((author) => {
    const { firstName, lastName, imageSmall, slug } = author;
    const title = [firstName, lastName].filter(Boolean).join(' ');
    const src = imageSmall?.replace('%3F', '?');
    const href = slug ? `/vara-skribenter/${slug}` : undefined;

    return {
      title,
      icon: { name: 'chevronRight' },
      image: { src, alt: title },
      link: { href },
    } as AuthorsListAuthor;
  });

  const pageTitle = `Våra skribenter ${titleSuffix}`;
  const pageDescription = `Här hittar du alla journalister som skriver på ${brandName} tidnings redaktion.`;

  return (
    <Layout
      content={
        <>
          <Head>
            <title>{pageTitle}</title>
            <meta name="title" content={pageTitle} key="metaTitle" />
            <meta name="description" content={pageDescription} key="metaDescription" />
            <link rel="canonical" href={canonical}></link>
          </Head>
          <SchemaMarkup
            pageData={{
              type: 'front',
              meta: {},
              data: {
                publishedUrl: '/vara-skribenter',
                seoTitle: pageTitle,
                seoDescription: pageDescription,
              },
              children: [],
            }}
          />

          <AuthorsList authors={authorsList} {...{ description, headline }} />
        </>
      }
    />
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { headline, description } = getOcelotConfig('pages.author');
  const authors = await getEligibleToShowAuthors();
  const titleSuffix = (await getLabradorConfig('miscsettings.site.siteTitleSuffix')) || '';

  withCacheControlHeaders(context.res, CLOUDFLARE_TTL);

  return {
    props: withSerializationGuard({
      authors,
      headline,
      description,
      titleSuffix,
    }),
  };
};

export default PageAuthors;
